import { useApps } from '@auth/useApps'
import SplitButton from '@components/button/SplitButton'
import { SplitButtonActionItem } from '@components/button/types'
import theme, { colors } from '@globals/themes/defaultTheme'
import { tabletQuery } from '@globals/themes/themeConstants'
import {
  Recognition,
  initiateRecognitionAttachmentUpload,
  listRecognitionRecipients,
} from '@kudos/http-client'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import { SxProps } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import { Selectable } from '@shared/filters/selections'
import FieldLabel from '@shared/forms/FieldLabel'
import FormActionTray from '@shared/forms/FormActionTray'
import PublicPrivateField from '@shared/forms/PublicPrivateField'
import QualitiesAutoCompleteField from '@shared/forms/QualitiesAutoCompleteField'
import Submit from '@shared/forms/Submit'
import TenantMembersAndTeamsSearchField from '@shared/forms/tenantMemberAndTeamSearchField/TenantMemberAndTeamSearchField'
import { AppPermission } from '@shared/permissions'
import stripHtml from '@shared/utils/stripHtml'
import useRecognitionModal, {
  RecognitionModalViews,
} from '@tenant/recognition/RecognitionModal/useRecognitionModal'
import { T } from '@transifex/react'
import { useFormContext, useWatch } from 'react-hook-form'
import MessageAndAttachmentsSection from './MessageAndAttachmentsSection'
import PointsSelectionField from './PointsSelectionField'
import SaveAsDraftButton from './SaveAsDraftButton'
import { RecognitionFormData } from './useSendRecognitionForm'

const formActionsStyle: SxProps = {
  position: 'absolute',
  bottom: 0,
  backgroundColor: colors.white,
  width: '100%',
  mx: -2,
  px: 2,
  py: 1,
  zIndex: theme.zIndex.modal + 1,
  [tabletQuery]: {
    position: 'fixed',
  },
}

type RecognitionFormProps = {
  recognition?: Recognition
  qualities?: Selectable[]
  isEditMode: boolean
  onSend: () => void
  onScheduleSend: () => void
  onSaveAsDraft: () => void
  delegatorId?: string
  isSavingAsDraft: boolean
}
const SendRecognitionForm: React.FC<RecognitionFormProps> = ({
  qualities,
  isEditMode,
  onSend,
  onScheduleSend,
  onSaveAsDraft,
  delegatorId,
  isSavingAsDraft,
}) => {
  const [hasAppPermission] = useApps()
  const canScheduleRecognition = hasAppPermission(
    'recognitionApp',
    AppPermission.RecognitionAppScheduleRecognition,
  )
  const canRefineMessage = hasAppPermission(
    'refinementApp',
    AppPermission.RefinementAppRefineRecognition,
  )
  const canUpgradeLicense = hasAppPermission(
    'refinementApp',
    AppPermission.RefinementAppUpgradeLicense,
  )

  const isTablet = useMediaQuery(tabletQuery)
  const { setView } = useRecognitionModal()
  const { control, formState, setValue } = useFormContext<RecognitionFormData>()
  const areButtonsDisabled = isSavingAsDraft || formState.isSubmitting || formState.isLoading
  const isDelegation = !!delegatorId
  const actions: SplitButtonActionItem[] = [
    {
      key: 'send',
      name: <T _str="Send" />,
      disabled: areButtonsDisabled,
      onClick: () => {
        setValue('scheduledAt', null)
        onSend()
      },
    },
    {
      key: 'scheduledSend',
      name: <T _str="Schedule send" />,
      disabled: !canScheduleRecognition,
      onClick: () => onScheduleSend(),
    },
  ]

  const sendTo = useWatch({ control, name: 'sendTo' })
  const message = useWatch({ control, name: 'message' })

  const showDraftButton = formState.isDirty && (sendTo.length > 0 || stripHtml(message).length > 0)

  return (
    <Stack>
      <Box pb={showDraftButton ? 16 : 8}>
        <TenantMembersAndTeamsSearchField
          control={control}
          name="sendTo"
          searchFn={listRecognitionRecipients}
          delegatorId={delegatorId}
          label={
            <FieldLabel labelStyle={{ mt: 0 }}>
              <T _str="To" />
            </FieldLabel>
          }
          showAvatars
          zIndexPlacement="modal"
        />
        <Box display="unset">
          <MessageAndAttachmentsSection
            control={control}
            canRefineMessage={canRefineMessage}
            canUpgradeLicense={canUpgradeLicense}
            attachmentUploadFn={initiateRecognitionAttachmentUpload}
            imageUploadFn={initiateRecognitionAttachmentUpload}
          />
          {qualities && qualities.length > 0 && (
            <QualitiesAutoCompleteField
              name="qualities"
              control={control}
              options={qualities || []}
            />
          )}
          <PointsSelectionField />
        </Box>
        <Stack
          direction="row"
          alignItems="baseline"
          justifyContent="space-between"
          sx={formActionsStyle}>
          <PublicPrivateField name="tenantWide" control={control} />
          <FormActionTray mt={1}>
            {isEditMode && (
              <Button
                id="btnCancelEditRecognition"
                variant="text"
                onClick={() =>
                  setView({ view: RecognitionModalViews.DraftAndInProgressRecognitions })
                }
                fullWidth={isTablet}>
                <T _str="Cancel" />
              </Button>
            )}
            {showDraftButton && !isDelegation && (
              <SaveAsDraftButton
                buttonId="btnSaveRecognitionAsDraft"
                buttonText={isEditMode ? <T _str="Save" /> : <T _str="Save as draft" />}
                variant="outlined"
                onSaveAsDraftClick={() => onSaveAsDraft()}
                fullWidth={isTablet}
                isDisabled={areButtonsDisabled}
              />
            )}
            {!isDelegation ? (
              <SplitButton
                elementId="splitBtn-sendOrScheduleRecognition"
                variant="contained"
                label={<T _str="Send" />}
                actions={actions}
                formState={formState}
              />
            ) : (
              <Submit
                elementId="btnSendRecognition"
                formState={formState}
                submitText={<T _str="Send" />}
              />
            )}
          </FormActionTray>
        </Stack>
      </Box>
    </Stack>
  )
}

export default SendRecognitionForm
